import { Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { Icons } from '../../../shared/helpers/icons';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { LucideAngularModule } from 'lucide-angular';
import { Router } from '@angular/router';
import { AssessTeamComponent } from '../../components/assess-team/assess-team.component';
import { ReviewRequestComponent } from '../../components/review-request/review-request.component';
import { EStage, EStatus } from '../../../shared/models/types.enum';
import { IAssessCase } from '../../../shared/models/assessCase';
import { AssessCaseService } from '../../../shared/services/assessCase.service';
import { UserDataService } from '../../../shared/services/auth/userData.service';
import { FormUtils } from '../../../shared/helpers/formUtils';
import { SpinnerComponent } from '../../components/spinner/spinner.component';
import { PopupMessageComponent } from '../../components/popup-message/popup-message.component';
import { ReviewCancelComponent } from '../../components/review-cancel/review-cancel.component';
import { RoarModalComponent } from '../../components/roar-modal/roar-modal.component';
import { IRoarAssignmentForm } from '../../../shared/models/roarAssignmentForm';
import { FormsModule } from '@angular/forms';
import { NgxPaginationModule } from 'ngx-pagination';
import { IThreatAssessmentTeam } from '../../../shared/models/contact';
import { RoarAssignmentFormService } from '../../../shared/services/roarAssignmentForm.service';
declare var initFlowbite: any;

@Component({
  selector: 'app-plans-dashboard',
  standalone: true,
  imports: [
    FormsModule,
    CommonModule,
    LucideAngularModule,
    AssessTeamComponent,
    ReviewRequestComponent,
    SpinnerComponent,
    PopupMessageComponent,
    ReviewCancelComponent,
    RoarModalComponent,
    NgxPaginationModule
  ],
  templateUrl: './plans-dashboard.component.html',
  styleUrl: './plans-dashboard.component.css'
})
export class PlansDashboardComponent implements OnInit {

  icons = Icons;
  stageEnum = EStage;
  statusEnum = EStatus;
  currentBuildingId: string | null = null;
  currentUser: IThreatAssessmentTeam | null = null;
  searchQuery: string = '';
  planCases: IAssessCase[] = [];
  filteredPlanCases: IAssessCase[] = [];
  selectedCase: IAssessCase | null = null;
  roarAssignments: IRoarAssignmentForm[] = [];
  messageModal: string | null = null;
  popupMessage: string | null = null;
  page = 1;
  itemsPerPage = 10;

  sortOrder: boolean = true;
  showTeam: boolean = false;
  showRoarModal: boolean = false;
  showReviewRequest: boolean = false;
  showReviewModal: boolean = false;
  showCancelReview: boolean = false;
  loading: boolean = false;
  
  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private router: Router,
    private assessCaseService: AssessCaseService,
    private roarAssignmentFormService: RoarAssignmentFormService,
    private userDataService: UserDataService,
    private formUtils: FormUtils
  ) {}
  
  ngOnInit(): void { 
    this.currentBuildingId = this.userDataService.getBuildingId();
    this.currentUser = this.userDataService.getCurrentUser();
    this.loadPlansCases();
  }

  loadPlansCases(): void {
    // get all case and filter by current stage at first loading
    if (this.currentBuildingId) {
      this.loading = true;
      this.assessCaseService.getBuildingAssessCases(this.currentBuildingId).subscribe({
        next: (response) => {
          this.loading = false;
          if (!response.message) {
            this.planCases = response.filter((item: IAssessCase) => item.current_stage === EStage.Plans);
            if (isPlatformBrowser(this.platformId)) { // refresh flowbite effects (tooltip) only in browser
              setTimeout(() => {
                initFlowbite();
              }, 100);
            }
            this.filteredPlanCases = this.planCases.length > 0 ? this.planCases.reverse() : [];
          } else {
            console.error(response.message);
          }
        },
        error: (error) => {
          this.loading = false;
          console.error('Error fetching building assessments cases:', error);
          this.popupMessage = error.message
          this.messageModal = 'unsuccess';
        }
      });
    } else {
      return
    }
  }

  sortList(key: keyof typeof this.filteredPlanCases[0]): void {
    this.sortOrder = !this.sortOrder;
    this.filteredPlanCases.sort((a, b) => {
      const valueA = a[key]?.toString().toLowerCase() || '';
      const valueB = b[key]?.toString().toLowerCase() || '';

      if (valueA < valueB) {
        return this.sortOrder ? -1 : 1;
      }
      if (valueA > valueB) {
        return this.sortOrder ? 1 : -1;
      }
      return 0;
    });
  }

  filterAssessCases(): void {
    this.filteredPlanCases = this.planCases.filter((caseItem) => {
      return (
        caseItem.case_id.toLowerCase()
          .includes(this.searchQuery.toLowerCase()) ||
        caseItem.detail_of_situation?.toLowerCase()
          .includes(this.searchQuery.toLowerCase()) ||
        caseItem.created_by_name?.toLowerCase()
          .includes(this.searchQuery.toLowerCase())
      );
    });
  }

  openModal(type: string, clickedCase?: IAssessCase): void {
    if (type === 'team') {
      this.showTeam = true;
    } else if (type === 'review request') {
      this.showReviewRequest = true;
    } else if (type === 'review modal') {
      this.showReviewModal = true;
    } else if (type === 'cancel review') {
      this.showCancelReview = true;
    } else if (type === 'roar') {
      this.showRoarModal = true;
    }
    this.selectedCase = clickedCase ? clickedCase : null;
  }

  closeModal(type: string): void {
    if (type === 'team') {
      this.showTeam = false;
    } else if (type === 'review request') {
      this.showReviewRequest = false;
    } else if (type === 'review modal') {
      this.showReviewModal = false;
    } else if (type === 'cancel review') {
      this.showCancelReview = false;
    } else if (type === 'roar') {
      this.showRoarModal = false;
    }
    this.selectedCase = null;
  } 
  
  handleSave(message: {type: string, message: string}, actionType?: string): void {
    if (actionType === 'team') {
      this.closeModal('team');
    } else if (actionType === 'review request') {
      this.closeModal('review request');
    } else if (actionType === 'cancel review') {
      this.closeModal('cancel review');
    }
    if (message.type === 'success') {
      this.loadPlansCases();
      this.messageModal = 'success';
      this.popupMessage = message.message;
      
    } else if (message.type === 'failed') {
      this.messageModal = 'unsuccess';
      this.popupMessage = message.message;
    }
  }
  
  isStageReviewPending(clickedCase: IAssessCase): boolean {
    if (clickedCase.rms_plan_checklist === this.statusEnum.ReviewPending || 
    clickedCase.roar_assignment_form === EStatus.ReviewPending || 
    clickedCase.rms_plan_review_schedule === EStatus.ReviewPending || 
    clickedCase.rms_plan_summary === EStatus.ReviewPending) {
      return true;
    }
    return false;
  }

  isAllRequiredFormsUndone(selectedCase: IAssessCase): boolean {
    if (selectedCase.threat_assessment_team_status === this.statusEnum.FormUndone || 
    selectedCase.rms_plan_checklist === this.statusEnum.FormUndone ||
    selectedCase.rms_plan_review_schedule === this.statusEnum.FormUndone ||
    selectedCase.rms_plan_summary === this.statusEnum.FormUndone) {
      return true;
    }   
    return false;
  }

  isUserInTeam(selectedCase: IAssessCase): boolean {
    return this.currentUser?.case_ids?.includes(selectedCase.case_id) ?? false;
  }
  
  goToPage(pageName: string, caseId: string, roarId?: string): void {
    this.router.navigate(['/plans/' + pageName + '/' + caseId + (roarId ? '/' + roarId : '')]);
  }
  
  getTooltip(fieldValue: number, context: string) {
    return this.formUtils.getTooltipContent(fieldValue, context);
  }
  
  resetMessageModal(): void {
    this.messageModal = null;
    this.popupMessage = null;
  }
}
